/**
 * The branches inside box 8, each with its director, deputy and divisions.
 *
 * The printed sheet has room for two shaded Branch blocks, so Add stops at two.
 * Deleting a branch takes its divisions with it, because a division's `section`
 * is the branch id and an orphan would print under nothing.
 */

import type React from 'react';
import { useState } from 'react';
import { Check, Plus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { ICS204_SECTION_TITLE } from '../AssignmentList/assignmentCardStyles';
import { Ics203DivisionList, type Ics203Division } from './Ics203DivisionList';

/** The number of shaded Branch blocks the printed form has room for. */
export const ICS203_MAX_BRANCHES = 2;

/** The part of a stored branch record box 8 prints. */
export type Ics203Branch = {
  id?: string;
  name?: string;
  branch_director_name?: string;
  branch_deputy?: string;
};

type BranchDraft = {
  name: string;
  branch_director_name: string;
  branch_deputy: string;
};

export type Ics203BranchListProps = {
  branches: readonly Ics203Branch[];
  divisions: readonly Ics203Division[];
  onCreateBranch: () => void;
  onUpdateBranch: (branch: Ics203Branch, patch: BranchDraft) => void;
  onDeleteBranch: (branch: Ics203Branch) => void;
  onCreateDivision: (branchId: string) => void;
  onRenameDivision: (division: Ics203Division, name: string) => void;
  onDeleteDivision: (id: string) => void;
  readOnly?: boolean;
};

export const Ics203BranchList: React.FC<Ics203BranchListProps> = ({
  branches,
  divisions,
  onCreateBranch,
  onUpdateBranch,
  onDeleteBranch,
  onCreateDivision,
  onRenameDivision,
  onDeleteDivision,
  readOnly = false,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<BranchDraft>({
    name: '',
    branch_director_name: '',
    branch_deputy: '',
  });

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      {branches.map((branch) => {
        const isEditing = editingId != null && editingId === branch.id;
        const label = branch.name?.trim() || 'this branch';
        const branchDivisions = divisions
          .filter((division) => division.section === branch.id)
          .sort((a, b) => (a.id || '').localeCompare(b.id || ''));

        return (
          <div
            key={branch.id}
            className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2 tw:border-l tw:border-[var(--border-primary)] tw:pl-3"
            data-testid={`ics203-branch-${branch.id}`}
          >
            <div className="tw:flex tw:min-w-0 tw:items-center tw:gap-2">
              <span className={ICS204_SECTION_TITLE}>Branch</span>
              {isEditing ? null : (
                <>
                  <button
                    type="button"
                    className="tw:min-w-0 tw:flex-1 tw:break-words tw:text-left tw:text-sm tw:underline-offset-2 tw:hover:underline tw:disabled:no-underline"
                    disabled={readOnly}
                    aria-label={`Edit ${label}`}
                    onClick={() => {
                      setEditingId(branch.id ?? null);
                      setDraft({
                        name: branch.name ?? '',
                        branch_director_name: branch.branch_director_name ?? '',
                        branch_deputy: branch.branch_deputy ?? '',
                      });
                    }}
                  >
                    {branch.name || '—'}
                  </button>
                  {readOnly ? null : (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={`Delete ${label}`}
                      onClick={() => onDeleteBranch(branch)}
                    >
                      <Trash2 className="tw:size-4" />
                    </Button>
                  )}
                </>
              )}
            </div>

            {isEditing ? (
              <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
                <Input
                  aria-label={`Branch name for ${label}`}
                  placeholder="Enter name..."
                  value={draft.name}
                  onChange={(event) =>
                    setDraft({ ...draft, name: event.target.value })
                  }
                />
                <Input
                  aria-label={`Branch Director for ${label}`}
                  placeholder="Branch Director"
                  value={draft.branch_director_name}
                  onChange={(event) =>
                    setDraft({
                      ...draft,
                      branch_director_name: event.target.value,
                    })
                  }
                />
                <Input
                  aria-label={`Deputy for ${label}`}
                  placeholder="Deputy"
                  value={draft.branch_deputy}
                  onChange={(event) =>
                    setDraft({ ...draft, branch_deputy: event.target.value })
                  }
                />
                <div className="tw:flex tw:justify-end tw:gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Cancel editing ${label}`}
                    onClick={() => setEditingId(null)}
                  >
                    <X className="tw:size-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Save ${label}`}
                    onClick={() => {
                      onUpdateBranch(branch, draft);
                      setEditingId(null);
                    }}
                  >
                    <Check className="tw:size-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="tw:grid tw:gap-1 tw:text-sm">
                <div>
                  <span className="tw:text-muted-foreground">
                    Branch Director:{' '}
                  </span>
                  {branch.branch_director_name || '—'}
                </div>
                <div>
                  <span className="tw:text-muted-foreground">Deputy: </span>
                  {branch.branch_deputy || '—'}
                </div>
              </div>
            )}

            <Ics203DivisionList
              testId={`ics203-branch-divisions-${branch.id}`}
              divisions={branchDivisions}
              readOnly={readOnly}
              onCreate={() => branch.id && onCreateDivision(branch.id)}
              onRename={onRenameDivision}
              onDelete={onDeleteDivision}
            />
          </div>
        );
      })}

      {readOnly ? null : (
        <div className="tw:flex tw:justify-end">
          <Button
            type="button"
            size="sm"
            disabled={branches.length >= ICS203_MAX_BRANCHES}
            onClick={onCreateBranch}
          >
            <Plus className="tw:size-4" />
            Add Branch
          </Button>
        </div>
      )}
    </div>
  );
};

export default Ics203BranchList;
